/**
 * Which organization the user is working in - the one value behind the switch
 * in the app bar, the X-Organization header on every request, and the
 * re-keyed route table (docs/changes/organization-scoping/SPEC.md section 3c).
 *
 * A plain module rather than React state so that apiClient's request
 * interceptor can read it; React subscribes through hooks/useOrganization.js.
 * The value lives in localStorage under one key: a preference, not a secret,
 * shared across tabs on purpose - the `storage` event keeps a second tab on
 * the same organization rather than letting it post under the other one.
 *
 * With nothing valid stored the first available organization is the
 * selection, so the header is never absent and the switch never shows nothing.
 * The first-run chooser (pages/ChooseOrganization.jsx) stands in front of that
 * default until someone chooses.
 *
 * "Available" is the configured organizations this user may use
 * (organization-scoping 3.6b): GET /my-organizations, loaded after sign-in by
 * apiClient's loadPermittedOrganizations and handed in here. Until it answers,
 * every configured one - the router is what enforces the list, with a 403, so
 * the screen only has to stop offering what it would refuse. A stored choice
 * the user may no longer use is ignored, so the selection moves to the first
 * one they may.
 */

import { brand } from "../config/brand";

export const ORGANIZATION_KEY = "warehouse-inventory.organization";

const listeners = new Set();

// The ids GET /my-organizations answered, or null before it has.
let permitted = null;

const ids = () =>
  brand.organizations
    .map((organization) => organization.id)
    .filter((id) => permitted === null || permitted.includes(id));

/** The configured organizations this user may use, in configured order. */
export function availableOrganizations() {
  const available = ids();
  return brand.organizations.filter((organization) =>
    available.includes(organization.id),
  );
}

const readStored = () => {
  try {
    return window.localStorage.getItem(ORGANIZATION_KEY);
  } catch {
    return null;
  }
};

/** The stored selection when it names an available organization, else null. */
export function chosenOrganization() {
  const stored = readStored();
  return stored && ids().includes(stored) ? stored : null;
}

/**
 * The current organization id - the stored choice, else the first available.
 * Null only for a user who may use none, whose requests then carry no header
 * and are refused; the app shows them a page saying so instead.
 */
export function getOrganization() {
  return chosenOrganization() ?? ids()[0] ?? null;
}

/** The configured entry (id, name, shortName, logo, logoAlt, remitTo) for an id. */
export function organizationOf(id) {
  return brand.organizations.find((organization) => organization.id === id);
}

const notify = () => {
  const current = getOrganization();
  for (const listener of listeners) listener(current);
};

/** Stores a selection and tells every subscriber. An unavailable id is refused. */
export function setOrganization(id) {
  if (!ids().includes(id)) {
    throw new Error(`Unknown organization "${id}"`);
  }
  if (id === getOrganization() && chosenOrganization() === id) return;
  try {
    window.localStorage.setItem(ORGANIZATION_KEY, id);
  } catch {
    // Storage blocked: the selection still applies for this page load,
    // because subscribers are told below and read through getOrganization()
    // only on their next render. Nothing to do here.
  }
  notify();
}

/**
 * The organizations this user may use, from GET /my-organizations; null for
 * every configured one. Ids the config does not list are dropped by `ids()`.
 * Tells every subscriber, because the selection may move with it.
 */
export function setPermittedOrganizations(list) {
  permitted = Array.isArray(list) ? [...list] : null;
  notify();
}

/** Calls `listener(id)` on every change, this tab's or another's. Returns the unsubscribe. */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* Another tab changed the stored value. */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === ORGANIZATION_KEY || event.key === null) notify();
  });
}

/**
 * True for a path that names one record - a job, a company, a tag, a BOL.
 *
 * Every route in App.jsx that carries a parameter has two or more segments
 * (/EditJob/:BillingCustomerID/:JobTicketID, /TagList/:TDControlNumber), and
 * every single-segment route is a list, a report or a blank create form. So
 * the segment count is the test, without a second copy of the route table:
 * switching organization on a record screen goes home, because that record
 * belongs to the other organization and has nothing to show (SPEC Q7).
 */
export function isRecordPath(pathname) {
  return (
    String(pathname ?? "")
      .split("/")
      .filter(Boolean).length > 1
  );
}
